import _isEmpty from 'lodash/isEmpty';
import {
  productDetailsService,
  productServiceWithInHouseData,
  productCatalogService,
  skuTrackerService,
  profileService,
  editStyleboards,
  cmdProfileServiceV3,
  inventoryStatusService,
  farfetchProductDetailsService,
  productCatalogFarfetchService,
} from 'config/api';
import { HtmlToText } from 'utils';
import { addNotification } from 'modules/shell/actions';
import { SIZES, SIZE_LABELS } from 'constants/filters';
import { adjustSearchParams, extendSkuInfoWithProductDetails, generateEditGalleryQueryParams } from '../utils';
import actionTypes from './actionTypes';

export const getProductDetails =
  (productId, { preSelectedColor, preSelectedSize } = {}, toggle) =>
  async (dispatch, getState) => {
    const { skuInfo } = getState().productsModule?.productDetails;
    const selectedSkuId = skuInfo.metadata?.pimSkuId;
    dispatch({
      type: actionTypes.PRODUCT_DETAILS_LOADING,
    });

    const version = toggle ? 'v4' : 'v3';

    try {
      const productDetailsResponse = await productDetailsService.get(`${version}/productdetails/${productId}`);

      const name = HtmlToText(productDetailsResponse.data.name);

      const successPayload = {
        ...productDetailsResponse.data,
        name,
      };

      // select default product sku depend on next criteria:
      let selectedSku;

      if (preSelectedColor || preSelectedSize) {
        selectedSku = productDetailsResponse.data.skus?.find((sku) => {
          if (preSelectedColor && preSelectedSize) {
            return sku.color.name === preSelectedColor && sku.size.name === preSelectedSize;
          }

          if (preSelectedColor) {
            return sku.color.name === preSelectedColor;
          }

          if (preSelectedSize) {
            return sku.size.name === preSelectedSize;
          }
          return false;
        });
      } else if (selectedSkuId) {
        selectedSku = productDetailsResponse.data.skus?.find((sku) => sku.metadata.pimSkuId === selectedSkuId);
      }

      if (selectedSku) {
        successPayload.skuInfo = {
          ...selectedSku,
          productId,
        };
      }
      dispatch({
        type: actionTypes.PRODUCT_DETAILS_SUCCESS,
        payload: successPayload,
      });
    } catch (error) {
      dispatch({
        type: actionTypes.PRODUCT_DETAILS_ERROR,
        payload: error,
      });
    }
  };

export const getFarfetchProductDetails =
  (id, { preSelectedColor, preSelectedSize } = {}) =>
  async (dispatch, getState) => {
    const { skuInfo } = getState().productsModule?.productDetails;
    const selectedSkuId = skuInfo.metadata?.pimSkuId;
    dispatch({
      type: actionTypes.PRODUCT_DETAILS_FARFETCH_LOADING,
    });

    try {
      const productDetailsResponse = await farfetchProductDetailsService.get(`/pal/style/${id}`);

      if (productDetailsResponse.data.error) {
        dispatch({
          type: actionTypes.PRODUCT_DETAILS_FARFETCH_ERROR,
          payload: productDetailsResponse.data,
        });
      } else {
        const name = HtmlToText(productDetailsResponse.data.name);

        const successPayload = {
          ...productDetailsResponse.data,
          name,
        };

        // select default product sku depend on next criteria:
        let selectedSku;

        if (preSelectedColor || preSelectedSize) {
          selectedSku = productDetailsResponse.data.skus?.find((sku) => {
            if (preSelectedColor && preSelectedSize) {
              return sku.color.name === preSelectedColor && sku.size.name === preSelectedSize;
            }

            if (preSelectedColor) {
              return sku.color.name === preSelectedColor;
            }

            if (preSelectedSize) {
              return sku.size.name === preSelectedSize;
            }
            return false;
          });
        } else if (selectedSkuId) {
          selectedSku = productDetailsResponse.data.skus?.find((sku) => sku.metadata.pimSkuId === selectedSkuId);
        }

        if (selectedSku) {
          successPayload.skuInfo = {
            ...selectedSku,
            id,
          };
        }
        dispatch({
          type: actionTypes.PRODUCT_DETAILS_FARFETCH_SUCCESS,
          payload: successPayload,
        });
      }
    } catch (error) {
      dispatch({
        type: actionTypes.PRODUCT_DETAILS_FARFETCH_ERROR,
        payload: error,
      });
    }
  };

export const getProductInventoryAvailablilty = (params) => async (dispatch) => {
  dispatch({
    type: actionTypes.PRODUCT_INVENTORY_LOADING,
  });

  try {
    const inventoryResponse = await inventoryStatusService.post('/retail/inventory', params);

    dispatch({
      type: actionTypes.PRODUCT_INVENTORY_SUCCESS,
      payload: inventoryResponse.data,
    });
  } catch (error) {
    dispatch({
      type: actionTypes.PRODUCT_INVENTORY_ERROR,
      payload: error,
    });
  }
};

export const getProductStoreInventory = (params) => async (dispatch) => {
  dispatch({
    type: actionTypes.PRODUCT_STORE_INVENTORY_LOADING,
  });

  try {
    const inventoryResponse = await inventoryStatusService.post('/store/inventory', params);

    dispatch({
      type: actionTypes.PRODUCT_STORE_INVENTORY_SUCCESS,
      payload: inventoryResponse.data,
    });
  } catch (error) {
    dispatch({
      type: actionTypes.PRODUCT_STORE_INVENTORY_ERROR,
      payload: error,
    });
  }
};

export const resetProductsInventoryAndStock = () => async (dispatch) => {
  dispatch({
    type: actionTypes.PRODUCT_INVENTORY_STORE_RESET,
  });
};

export const setPerishableProductDeliveryDate = (deliveryDate) => ({
  type: actionTypes.PRODUCT_DETAILS_SET_DELIVERY_DATE,
  payload: deliveryDate,
});

export const setSkuInfo = (skuInfo) => ({
  type: actionTypes.PRODUCT_DETAILS_SET_SKU_INFO,
  payload: skuInfo,
});

export const resetProductDetails = () => ({
  type: actionTypes.PRODUCT_INVENTORY_STORE_RESET,
});

export const setExtendedSkuInfo =
  (skuInfo, { storeInventory = [], farFetchCheck = false, inventory = [] }) =>
  (dispatch, getState) => {
    const productDetails = farFetchCheck
      ? getState().productsModule?.productDetails.farfetchProductData
      : getState().productsModule?.productDetails.data;

    if (!_isEmpty(productDetails)) {
      dispatch(
        setSkuInfo(
          extendSkuInfoWithProductDetails(productDetails, skuInfo, { storeInventory, farFetchCheck, inventory }),
        ),
      );
    }
  };

export const getProductInventory =
  (skuInfo = {}) =>
  async (dispatch) => {
    try {
      const res = await productServiceWithInHouseData.get(`?product_id=${skuInfo.metadata?.pimSkuId}`);
      const productInfo = res.data?.[0] || {};
      const webProductId =
        productInfo.webProductSku && productInfo.webProductSku.slice(0, productInfo.webProductSku.indexOf('/'));

      const webProductSku =
        productInfo.webProductSku && productInfo.webProductSku.slice(productInfo.webProductSku.indexOf('/') + 1);

      const compSku = skuInfo.id || webProductSku;
      const skuForInventory = productInfo.skuStores?.find((e) => e.skuId === compSku);

      dispatch({
        type: actionTypes.PRODUCT_DETAILS_SET_SKU_INFO,
        payload: {
          ...skuInfo,
          stockLevel: productInfo.inventory,
          expectedShipDate: productInfo.expectedShipDate || skuInfo.expectedShipDate,
          dropShip: productInfo.dropshipFlag === 'Y',
          price: {
            retailPrice: productInfo.price?.currentPrice || skuInfo.price?.retailPrice || '',
          },
          store: productInfo.store || [],
          skuInfoError: productInfo.error,
          restrictedItem: productInfo.isRestricted,
          skuStores: productInfo?.skuStores || [],
          skuInventory: skuForInventory?.storeInventories || [],
          webProductSku: webProductSku || skuInfo.id,
          webProductId: webProductId || skuInfo.productId,
          paddedInventory: productInfo.paddedInventory,
        },
      });

      return { productId: webProductId || '' };
    } catch (error) {
      dispatch({
        type: actionTypes.PRODUCT_DETAILS_SET_SKU_INFO,
        payload: skuInfo,
      });
      return {};
    }
  };

export const getProductCategoriesLazy =
  ({ options: { from, size, searchValue }, category = '', parentCategoryId = '', forceDisplayName = false }) =>
  async (dispatch, getState) => {
    const { data: currentData } = getState().productsModule.productCategory;
    const { brand } = getState().user.data;

    const params = { from, size, brand };

    if (category) {
      params.categoryId = category;
    }

    if (parentCategoryId) {
      params.parentCategoryId = parentCategoryId;
    }

    if (searchValue) {
      params.q = searchValue;
    }

    if (forceDisplayName) {
      params.returnAlternateName = false;
    }

    dispatch({
      type: actionTypes.PRODUCT_CATEGORY_INFINITE_LIST_LOADING,
    });

    try {
      const { data } = await productCatalogService.get('/v3/categories', { params });
      if (searchValue) {
        dispatch({
          type: actionTypes.PRODUCT_CATEGORY_SUCCESS,
          payload: data,
        });
        return;
      }

      const currentChildren = currentData && currentData[0] && currentData[0].children;
      const children = currentChildren || Array.from(Array(data[0].childrenCount));

      // merging new portion of data
      for (let i = params.from, j = 0; i <= params.from + params.size; i++, j++) {
        if (data[0].children[j]) {
          children[i] = data[0].children[j];
        }
      }

      dispatch({
        type: actionTypes.PRODUCT_CATEGORY_SUCCESS,
        payload: [
          {
            ...data[0],
            children,
          },
        ],
      });
    } catch (error) {
      dispatch({
        type: actionTypes.PRODUCT_CATEGORY_ERROR,
        payload: error,
      });
    }
  };

export const getProductCategories =
  ({ category = '', parentCategoryId = '', startsFrom = '', endsAt = '', isBgFarfetch = false }) =>
  async (dispatch, getState) => {
    const { data: currentData } = getState().productsModule.productCategory;
    const { brand } = getState().user.data;
    const catId = currentData && currentData[0] && currentData[0].id;
    dispatch({
      type: actionTypes.PRODUCT_CATEGORY_LOADING,
    });

    let categorySearch = '';
    let parentCategory = '';
    let from = '';
    let size = '';

    if (category) {
      categorySearch = `&categoryId=${category}`;
    }
    if (parentCategoryId) {
      parentCategory = `&parentCategoryId=${parentCategoryId}`;
    }
    if (startsFrom !== '') {
      from = `&from=${startsFrom}`;
    }
    if (endsAt !== '') {
      size = `&size=${endsAt}`;
    }

    try {
      const productCatalogServiceCall = isBgFarfetch ? productCatalogFarfetchService : productCatalogService;
      const version = isBgFarfetch ? 'v1' : 'v3';
      const res = await productCatalogServiceCall.get(
        `/${version}/categories?brand=${brand}${categorySearch}${parentCategory}${from}${size}`,
      );
      dispatch({
        type: actionTypes.PRODUCT_CATEGORY_SUCCESS,
        payload: res.data,
      });

      if (startsFrom && res.data[0] && res.data[0].id && catId === res.data[0].id) {
        const currentChildren = currentData && currentData[0] && currentData[0].children;
        const children = currentChildren || Array.from(Array(res.data[0].childrenCount));

        //merging new portion of data
        for (let i = startsFrom, j = 0; i <= startsFrom + endsAt; i++, j++) {
          if (res.data[0].children[j]) {
            children[i] = res.data[0].children[j];
          }
        }

        dispatch({
          type: actionTypes.PRODUCT_CATEGORY_SUCCESS,
          payload: [
            {
              ...res.data[0],
              children,
            },
          ],
        });
      }
    } catch (error) {
      dispatch({
        type: actionTypes.PRODUCT_CATEGORY_ERROR,
        payload: error,
      });
    }
  };

/*
 * @params - params {
    categoryId,
    page,
    pageSize,
    text,
    brands,
    sizes,
    colors,
    minPrice,
    maxPrice,
    priceTypes,
    patterns,
    styles,
    occasions,
    availability,
    sortBy,
    sortDirection,
  }
 */

export const getListOfProducts =
  (params, farfetchCheck = false) =>
  async (dispatch) => {
    const apiVersion = farfetchCheck ? 1 : 5;
    const apiEndpoint = farfetchCheck
      ? productCatalogFarfetchService.post(`/v${apiVersion}/product`, adjustSearchParams(params))
      : productCatalogService.post(`/v${apiVersion}/product`, adjustSearchParams(params));
    if (params.page === 0) {
      dispatch({
        type: actionTypes.PRODUCT_RESET_PAGE_NUMBER,
      });
      dispatch({
        type: actionTypes.PRODUCT_LISTING_LOADING,
      });
    }
    try {
      const res = await apiEndpoint;
      dispatch({
        type: params.page === 0 ? actionTypes.PRODUCT_LISTING_SUCCESS : actionTypes.PRODUCT_LISTING_UPDATE,
        payload: {
          products: res.data?.list,
          totalCount: res.data?.totalCount || 0,
          categoryName: res.data?.categoryName || '',
        },
      });
    } catch (error) {
      dispatch({
        type: actionTypes.PRODUCT_LISTING_ERROR,
        payload: error,
      });
    }
  };

export const getFilterOptions =
  (params, farfetchCheck = false) =>
  async (dispatch) => {
    const apiVersion = farfetchCheck ? 1 : 5;
    const apiEndpoint = farfetchCheck
      ? productCatalogFarfetchService.post(`/v${apiVersion}/filter-options`, adjustSearchParams(params))
      : productCatalogService.post(`/v${apiVersion}/filter-options`, adjustSearchParams(params));

    dispatch({
      type: actionTypes.FILTER_OPTIONS_LOADING,
    });

    try {
      const res = await apiEndpoint;

      const filterLabels = {};
      res.data.brands.forEach((el) => {
        filterLabels[el.key] = el.title;
      });

      res.data.sizes.forEach((el) => {
        filterLabels[el.key] = el.title;
      });

      dispatch({
        type: actionTypes.SET_FILTER_LABELS,
        payload: filterLabels,
      });
      dispatch({
        type: actionTypes.FILTER_OPTIONS_SUCCESS,
        payload: res.data,
      });
    } catch (error) {
      dispatch({
        type: actionTypes.FILTER_OPTIONS_ERROR,
        payload: error,
      });
    }
  };

export const setEditGallerySearchValue = (searchValue) => ({
  type: actionTypes.SET_EDIT_GALLERY_SEARCH_VALUE,
  payload: searchValue,
});

export const setEditGalleryActiveFilters = (activeFilters) => ({
  type: actionTypes.SET_EDIT_GALLERY_ACTIVE_FILTERS,
  payload: activeFilters,
});

export const getEditGalleryFilterOptions = (params) => async (dispatch) => {
  const editGalleryFilterParams = generateEditGalleryQueryParams(params);

  dispatch({
    type: actionTypes.EDIT_GALLERY_LOADING,
  });

  try {
    const res = await editStyleboards.get(`/v5/styleboards-aggregations?${editGalleryFilterParams}`);

    dispatch({
      type: actionTypes.EDIT_GALLERY_FILTERS_SUCCESS,
      payload: res.data.aggregations,
    });
  } catch (error) {
    dispatch({
      type: actionTypes.EDIT_GALLERY_ERROR,
      payload: error,
    });
  }
};

export const getListOfEditGalleryItems = (params) => async (dispatch) => {
  const editGalleryQueryParams = generateEditGalleryQueryParams(params);

  dispatch({
    type: actionTypes.EDIT_GALLERY_LOADING,
  });

  try {
    const res = await editStyleboards.get(`/v4/styleboards?${editGalleryQueryParams}`);
    const unixTimestamp = Math.round(+new Date() / 1000);

    // Appending ?t=timestamp to imageUrl, so that image will load instead of taking from cache
    res.data.styleboards = res.data.styleboards.map((styleboard) => {
      const result = { ...styleboard };
      if (result?.imageUrl?.length) {
        result.imageUrl += `?t=${unixTimestamp}`;
      }
      return result;
    });

    dispatch({
      type: params.page === 0 ? actionTypes.EDIT_GALLERY_SUCCESS : actionTypes.EDIT_GALLERY_UPDATE,
      payload: {
        data: res.data.styleboards,
        categoryId: params.categories,
      },
    });
  } catch (error) {
    dispatch({
      type: actionTypes.EDIT_GALLERY_ERROR,
      payload: error,
    });
  }
};

export const resetListOfProducts = () => ({
  type: actionTypes.PRODUCT_LISTING_RESET,
});

export const productAddToBagNoProfileData = () => (dispatch) => {
  dispatch(
    addNotification({
      id: `info-${Date.now()}`,
      message: "Client doesn't have any profile",
      type: 'info',
    }),
  );

  dispatch({ type: actionTypes.PRODUCT_ADD_TO_BAG_NO_PROFILE_DATA });
};

export const setPageNumber = (pageNumber) => (dispatch) => {
  dispatch({
    type: actionTypes.PRODUCT_SET_PAGE_NUMBER,
    payload: {
      pageNumber,
    },
  });
};

export const setFilterCategory =
  ({ category, data }) =>
  (dispatch) => {
    dispatch({
      type: actionTypes.SET_FILTER_CATEGORY,
      payload: {
        category,
        data,
      },
    });
  };

export const setMultipleFilters = (filters) => (dispatch) => {
  Object.keys(filters).forEach((key) =>
    dispatch(
      setFilterCategory({
        category: key,
        data: filters[key],
      }),
    ),
  );
};

export const setNonFilterPlPSelection =
  ({ category, data }) =>
  (dispatch) => {
    dispatch({
      type: actionTypes.SET_NON_FILTER_PLP_SELECTION,
      payload: {
        category,
        data,
      },
    });
  };

export const clearNonFilterPlPSelection = (category) => (dispatch) => {
  dispatch({
    type: actionTypes.CLEAR_NON_FILTER_PLP_SELECTION,
    payload: { category },
  });
};

export const clearFilterCategory = (category, value) => (dispatch) => {
  const payload = { category, value };
  payload.separator = category === SIZES || category === SIZE_LABELS ? '|' : ',';

  dispatch({
    type: actionTypes.CLEAR_FILTER_CATEGORY,
    payload,
  });
};

export const clearMultipleFilters = (filters) => (dispatch) => {
  filters.forEach((category) => dispatch(clearFilterCategory(category)));
};

export const clearAllFilters = (leaveText) => (dispatch) => {
  dispatch({
    type: actionTypes.CLEAR_ALL_FILTERS,
    payload: {
      leaveText,
    },
  });
};

export const setCategoryId = (categoryId) => ({
  type: actionTypes.SET_CATEGORY_ID,
  payload: categoryId,
});

export const addRecentProductSearch = (value) => ({
  type: actionTypes.RECENT_PRODUCT_SEARCH_ADD,
  payload: value,
});

export const clearRecentProductSearch = () => ({
  type: actionTypes.RECENT_PRODUCT_SEARCH_CLEAR,
});

export const getTrackerDetails = () => async (dispatch) => {
  dispatch({
    type: actionTypes.TRACKERS_LOADING,
  });

  try {
    const res = await skuTrackerService.get('/v1/skuTrackers');
    dispatch({
      type: actionTypes.TRACKERS_SUCCESS,
      payload: res,
    });
  } catch (error) {
    dispatch({
      type: actionTypes.TRACKERS_ERROR,
      payload: error,
    });
  }
};

export const getSkuCMDProfileMap =
  (cmdUriArr, isSkuTrackerProductShareViaChatEnabled = false) =>
  async (dispatch) => {
    dispatch({
      type: actionTypes.TRACKERS_PROFILES_LOADING,
    });

    try {
      const response = await Promise.all(
        cmdUriArr.map(async (cmdUri) => {
          let apiInfo;
          if (isSkuTrackerProductShareViaChatEnabled) {
            const additionalQueryString = '&internalStorage=true';
            apiInfo = cmdProfileServiceV3.get(`profiles/${cmdUri}?fields=basic,onlineProfiles${additionalQueryString}`);
          } else {
            apiInfo = profileService.get(`profiles/${cmdUri}/info`);
          }
          const profile = await apiInfo;
          return { ...profile.data, cmdUri };
        }),
      );
      dispatch({
        type: actionTypes.TRACKERS_PROFILES_SUCCESS,
        payload: response,
      });
    } catch (error) {
      dispatch({
        type: actionTypes.TRACKERS_ERROR,
        payload: error,
      });
    }
  };

export const getTrackerProductDetails = (skuId) => async (dispatch) => {
  dispatch({
    type: actionTypes.TRACKERS_LOADING,
  });

  try {
    const res = await skuTrackerService.get(`/v1/skuTrackers/product/${skuId}`);
    dispatch({
      type: actionTypes.TRACKERS_SET_PRODUCT_DETAILS,
      payload: res.data[0],
    });
  } catch (error) {
    dispatch({
      type: actionTypes.TRACKERS_ERROR,
      payload: error,
    });
  }
};

export const setSkuTrackerClients = (arr) => ({
  type: actionTypes.TRACKERS_SET_CLIENTS,
  payload: arr,
});

export const removeTrackerProfile = (cmdUri) => (dispatch) => {
  dispatch({
    type: actionTypes.REMOVE_TRACKER_PROFILE,
    payload: cmdUri,
  });
};

export const removeTracker = (sku) => (dispatch) => {
  dispatch({
    type: actionTypes.REMOVE_TRACKER,
    payload: sku,
  });
};

export const restoreSkuTrackerState = (skuTrackerState) => ({
  type: actionTypes.RESTORE_TRACKERS_STATE,
  payload: skuTrackerState,
});

export const getVendorRestrictionsById = (vendorId) => async (dispatch) => {
  dispatch({
    type: actionTypes.VENDOR_DELIVERY_DATE_RESTRICTIONS_LOADING,
  });

  try {
    const { data } = await productDetailsService.get(`/v1/vendor/${vendorId}/restrictions`);

    dispatch({
      type: actionTypes.VENDOR_DELIVERY_DATE_RESTRICTIONS_SUCCESS,
      payload: {
        restrictions: data,
        vendorId,
      },
    });
  } catch (error) {
    dispatch({
      type: actionTypes.VENDOR_DELIVERY_DATE_RESTRICTIONS_ERROR,
      payload: error,
    });
  }
};

export const clearVendorRestrictions = () => ({
  type: actionTypes.VENDOR_DELIVERY_DATE_RESTRICTIONS_CLEAR,
});
